import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthContext } from '../contexts/AuthContext';
import { sendLoginNotification } from '../services/loginNotificationService';
import { getApiBaseUrl, detectRegion } from '../config/region';

// Get dynamic API base URL and region
const API_BASE_URL = getApiBaseUrl();
const CURRENT_REGION = detectRegion();

export const useAuth = () => {
  return useAuthContext();
};

// Helper function to get stored QR source data
const getStoredSource = () => {
  try {
    const stored = localStorage.getItem('hl_src');
    if (stored) {
      const parsed = JSON.parse(stored);
      console.log('✓ Retrieved QR source from localStorage:', parsed);
      return parsed;
    }
  } catch (e) {
    console.error('Error reading hl_src:', e);
  }
  return null;
};

// Fetch user data function for React Query
const fetchUserDataQuery = async () => {
  const token = localStorage.getItem('venue_app_token');
  if (!token) {
    throw new Error('No token found');
  }

  // ✅ PHASE 2: Fixed double /api - API_BASE_URL already includes /api
  const response = await fetch(`${API_BASE_URL}/users/me`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'x-region': CURRENT_REGION,
    },
  });
  
  if (!response.ok) {
    clearAuthData();
    throw new Error('Invalid token');
  }

  return response.json();
};

// React Query hook for user data with caching
export const useUserData = () => {
  return useQuery({
    queryKey: ['user-data'],
    queryFn: fetchUserDataQuery,
    staleTime: 10 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
    enabled: !!localStorage.getItem('venue_app_token'),
  });
};

// Send OTP via Phone
export const sendOTP = async ({ phone, region = CURRENT_REGION }) => {
  try {
    // ✅ PHASE 2: Fixed double /api - API_BASE_URL already includes /api
    const response = await fetch(`${API_BASE_URL}/auth/send-otp`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-region': region,
      },
      body: JSON.stringify({ phone, region }),
    });
    
    const data = await response.json();
    return { 
      success: response.ok, 
      data, 
      error: response.ok ? null : (data.message || data.error || 'Failed to send OTP')
    };
  } catch (error) {
    console.error('Send OTP error:', error);
    return { success: false, error: 'Failed to send OTP' };
  }
};

export const useSendOTP = () => {
  return useMutation({
    mutationFn: sendOTP,
  });
};

// Verify OTP for Phone (with QR source tracking)
export const verifyOTP = async ({ phone, otp, region = CURRENT_REGION, investorType }) => {
  try {
    const hl_src = getStoredSource();
    
    // ✅ PHASE 2: Fixed double /api - API_BASE_URL already includes /api
    const response = await fetch(`${API_BASE_URL}/auth/verify-otp`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-region': region,
      },
      body: JSON.stringify({ 
        phone, 
        otp, 
        region,
        hl_src
      }),
    });
    
    const data = await response.json();
    
    if (response.ok && data.token) {
      localStorage.setItem('venue_app_token', data.token);
      localStorage.setItem('user_region', region);

      if (data.loginMethod) {
        localStorage.setItem('user_login_method', data.loginMethod);
      }
      if (data.phone) {
        localStorage.setItem('user_phone', data.phone);
      }
      if (data.name) {
        localStorage.setItem('user_name', data.name);
      }
      if (data.email) {
        localStorage.setItem('user_email', data.email);
      }
      if (data.role) {
        localStorage.setItem('user_role', data.role);
      }
      if (data.userId) {
        localStorage.setItem('user_id', data.userId);
      }
      if (data.hl_source_token) {
        localStorage.setItem('user_qr_token', data.hl_source_token);
        console.log('✓ QR token stored:', data.hl_source_token);
      }
      // Patrick rule 2026-04-26: attribute referrals on signup.
      // captureRefIfPresent stashed token on App mount; link new user.
      if (data.userId) {
        try {
          const mod = await import('../utils/shareLink');
          mod.markRefConverted(data.userId, 'signup');
        } catch (e) {}
      }
      
      // Send login notification
      if (investorType) {
        try {
          await sendLoginNotification({
            email: data.email || `${phone}@phone.user`,
            name: data.name || `User ${phone.slice(-4)}`,
            investorType: investorType,
            loginMethod: 'phone',
            region: region,
            timestamp: new Date().toLocaleString()
          });
        } catch (notificationError) {
          console.error('Failed to send login notification:', notificationError);
        }
      }
      
      setTimeout(() => window.location.reload(), 100);
      
      return { success: true, data };
    } else {
      return { 
        success: false, 
        error: data.message || data.error || 'Invalid OTP'
      };
    }
  } catch (error) {
    console.error('Verify OTP error:', error);
    return { success: false, error: 'Failed to verify OTP' };
  }
};

export const useVerifyOTP = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: verifyOTP,
    onSuccess: (data) => {
      if (data.success) {
        queryClient.invalidateQueries({ queryKey: ['user-data'] });
      }
    },
  });
};

// Send OTP via Email
export const sendEmailOTP = async ({ email, phone, region = CURRENT_REGION }) => {
  try {
    console.log('🎯 Sending email OTP:', { email, phone, region });
    
    // ✅ PHASE 2: Fixed double /api - API_BASE_URL already includes /api
    const response = await fetch(`${API_BASE_URL}/auth/send-email-otp`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'x-region': region,
      },
      body: JSON.stringify({ email, phone, region }),
    });
    
    const data = await response.json();
    
    return { 
      success: response.ok, 
      data, 
      error: response.ok ? null : (data.message || data.error || 'Failed to send email OTP')
    };
  } catch (error) {
    console.error('🎯 Send email OTP error:', error);
    return { success: false, error: 'Failed to send email OTP' };
  }
};

export const useSendEmailOTP = () => {
  return useMutation({
    mutationFn: sendEmailOTP,
  });
};

// Verify OTP for Email (with QR source tracking)
export const verifyEmailOTP = async ({ email, phone, otp, region = CURRENT_REGION, investorType }) => {
  try {
    console.log('🎯 Verifying email OTP:', { email, otp: otp.substring(0, 2) + '****', region });
    
    const hl_src = getStoredSource();
    
    // ✅ PHASE 2: Fixed double /api - API_BASE_URL already includes /api
    const response = await fetch(`${API_BASE_URL}/auth/verify-email-otp`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'x-region': region,
      },
      body: JSON.stringify({ 
        email, 
        phone,
        otp, 
        region,
        hl_src
      }),
    });
    
    const data = await response.json();
    
    if (response.ok && data.token) {
      localStorage.setItem('venue_app_token', data.token);
      localStorage.setItem('user_region', region);
      
      if (data.loginMethod) {
        localStorage.setItem('user_login_method', data.loginMethod);
      }
      if (data.email) {
        localStorage.setItem('user_email', data.email);
      }
      if (data.name) {
        localStorage.setItem('user_name', data.name);
      }
      if (data.phone) {
        localStorage.setItem('user_phone', data.phone);
      }
      if (data.role) {
        localStorage.setItem('user_role', data.role);
      }
      if (data.hl_source_token) {
        localStorage.setItem('user_qr_token', data.hl_source_token);
        console.log('✓ QR token stored:', data.hl_source_token);
      }
      if (data.userId) {
        localStorage.setItem('user_id', data.userId);
      }
      // Phase 2 referrals: mirror phone-OTP path. Stashed ?ref= token is
      // attributed to this new userId on signup completion.
      if (data.userId) {
        try {
          const mod = await import('../utils/shareLink');
          mod.markRefConverted(data.userId, 'signup');
        } catch (e) {}
      }

      // Send login notification
      if (investorType) {
        try {
          await sendLoginNotification({
            email: data.email || email,
            name: data.name || email.split('@')[0],
            phone: data.phone || phone,
            investorType: investorType,
            loginMethod: 'email',
            region: region,
            timestamp: new Date().toLocaleString()
          });
        } catch (notificationError) {
          console.error('Failed to send login notification:', notificationError);
        }
      }
      
      setTimeout(() => window.location.reload(), 100);
      
      return { success: true, data };
    } else {
      console.error('🎯 Email OTP verification failed:', { status: response.status, data });
      return { 
        success: false, 
        error: data.message || data.error || `Verification failed (${response.status})`
      };
    }
  } catch (error) {
    console.error('🎯 Verify email OTP error:', error);
    return { success: false, error: 'Failed to verify email OTP - Network error' };
  }
};

export const useVerifyEmailOTP = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: verifyEmailOTP,
    onSuccess: (data) => {
      if (data.success) {
        queryClient.invalidateQueries({ queryKey: ['user-data'] });
      }
    },
  });
};

// Fetch current user data (legacy)
export const fetchUserData = async () => {
  try {
    const token = localStorage.getItem('venue_app_token');
    if (!token) {
      return { success: false, error: 'No token found' };
    }

    // ✅ PHASE 2: Fixed double /api - API_BASE_URL already includes /api
    const response = await fetch(`${API_BASE_URL}/users/me`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'x-region': CURRENT_REGION,
      },
    });
    
    if (response.ok) {
      const userData = await response.json();
      return { success: true, data: userData };
    } else {
      clearAuthData();
      return { success: false, error: 'Invalid token' };
    }
  } catch (error) {
    console.error('Failed to fetch user data:', error);
    return { success: false, error: 'Failed to fetch user data' };
  }
};

// Clear all authentication data
export const clearAuthData = () => {
  localStorage.removeItem('venue_app_token');
  localStorage.removeItem('user_login_method');
  localStorage.removeItem('user_email');
  localStorage.removeItem('user_phone');
  localStorage.removeItem('user_name');
  localStorage.removeItem('user_role');
  localStorage.removeItem('user_region');
  localStorage.removeItem('user_qr_token');
  localStorage.removeItem('otp_flow_state');
  localStorage.removeItem('hl_src');
};

// Logout function
export const logout = () => {
  clearAuthData();
  window.location.reload();
};

export const useLogout = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async () => {
      clearAuthData();
      return { success: true };
    },
    onSuccess: () => {
      queryClient.clear();
      window.location.reload();
    },
  });
};

// Check if user is authenticated
export const isAuthenticated = () => {
  const token = localStorage.getItem('venue_app_token');
  return !!token;
};

// Get stored user login method
export const getUserLoginMethod = () => {
  return localStorage.getItem('user_login_method');
};

export const useRequireAuth = () => {
  const { user, loading } = useAuth();
  
  if (loading) return { user: null, loading: true, authenticated: false };
  if (!user) return { user: null, loading: false, authenticated: false };
  
  return { user, loading: false, authenticated: true };
};

export const useAuthStatus = () => {
  const { user, loading } = useAuth();
  
  return {
    isAuthenticated: !!user,
    isLoading: loading,
    user,
    hasRole: (role) => user?.role === role,
    isAdmin: user?.role === 'ADMIN',
    isConsumer: user?.role === 'CONSUMER',
    isStaff: user?.role === 'STAFF',
    isAgent: user?.role === 'AGENT',
    loginMethod: user?.loginMethod || getUserLoginMethod(),
    region: CURRENT_REGION,
  };
};
